import React, { useState } from "react";
import { Archive, Plus, Save, Trash2 } from "lucide-react";
import { api } from "./api";
import { useConfirmation } from "./Confirmation";
import { Badge, BoutonSombre, Section } from "./ui";

const AUJOURDHUI = new Date().toISOString().slice(0, 10);
/* Un stage dont le dernier jour est passé est archivé tout seul : l'école n'a rien à faire. */
const termine = (s) => !!(s.fin && s.fin < AUJOURDHUI);

/* Catalogue des stages : c'est lui qui décide du prix facturé. Tarif membre pour un élève
   qui suit déjà un cours semestriel, non-membre sinon. */
export default function CatalogueStages({ valeurs, onEnregistre }) {
  const { informer, demander } = useConfirmation();
  const [liste, setListe] = useState(valeurs.stages || []);
  const [voirTermines, setVoirTermines] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [err, setErr] = useState("");

  const maj = (i, champ) => (e) => setListe(liste.map((s, k) => (
    k === i ? { ...s, [champ]: champ.startsWith("prix") ? Number(e.target.value || 0) : e.target.value } : s)));

  const enregistrer = async () => {
    setOccupe(true);
    setErr("");
    try {
      await api("/reglages", { method: "PATCH", body: JSON.stringify({ valeurs: { stages: liste } }) });
      await onEnregistre();
      await informer({ titre: "Catalogue des stages enregistré", ton: "succes",
        texte: "Les factures des stages utiliseront ces prix." });
    } catch (e) { setErr(e.message); }
    setOccupe(false);
  };

  const supprimer = async (i) => {
    const s = liste[i];
    if (!await demander({
      titre: `Supprimer « ${s.nom || "cette ligne"} »`,
      ton: "danger",
      texte: "À n'utiliser que pour une ligne créée par erreur ou un stage annulé avant toute inscription.",
      points: ["Un stage terminé n'a pas besoin d'être supprimé : il s'archive tout seul après sa date de fin.",
        "Si des factures de ce stage existent, garde la ligne : c'est elle qui porte le prix."],
      valider: "Supprimer la ligne",
    })) return;
    setListe(liste.filter((_, k) => k !== i));
  };

  const actifs = liste.filter((s) => !termine(s));
  const passes = liste.filter(termine);
  const visibles = voirTermines ? liste : actifs;

  return (
    <Section titre="Stages" testid="reglages-stages"
      aide="Nom, dates et prix de chaque stage. La facturation des stages lit ces montants : tarif membre pour un élève déjà inscrit à un cours semestriel, non-membre sinon."
      action={
        <BoutonSombre onClick={enregistrer} disabled={occupe} testid="stages-catalogue-enregistrer">
          <Save size={14} /> Enregistrer
        </BoutonSombre>
      }>
      {err && <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-[var(--muted)]">
          {actifs.length} stage(s) en cours ou à venir
          {passes.length > 0 && ` · ${passes.length} archivé(s) automatiquement`}
        </span>
        {passes.length > 0 && (
          <button className="btn-ghost" data-testid="stages-voir-termines"
            onClick={() => setVoirTermines(!voirTermines)}>
            <Archive size={13} /> {voirTermines ? "Masquer les stages terminés" : "Voir les stages terminés"}
          </button>
        )}
      </div>

      <div className="space-y-3">
        {visibles.map((s) => {
          const i = liste.indexOf(s);
          return (
            <div key={s.id || i} className="bg-white rounded-2xl border p-4 grid md:grid-cols-6 gap-3"
              style={{ borderColor: "var(--border)", opacity: termine(s) ? 0.7 : 1 }}
              data-testid={`stage-catalogue-${i}`}>
              <label className="md:col-span-2 block">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Nom du stage</span>
                <input className="input-field mt-1" value={s.nom || ""} onChange={maj(i, "nom")}
                  data-testid={`stage-nom-${i}`} />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Dates (texte affiché)</span>
                <input className="input-field mt-1" value={s.dates || ""} onChange={maj(i, "dates")}
                  data-testid={`stage-dates-${i}`} />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Dernier jour</span>
                <input className="input-field mt-1" type="date" value={s.fin || ""} onChange={maj(i, "fin")}
                  data-testid={`stage-fin-${i}`} />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Prix membre (CHF)</span>
                <input className="input-field mt-1" type="number" value={s.prix_membre || 0}
                  onChange={maj(i, "prix_membre")} data-testid={`stage-prix-membre-${i}`} />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Prix non-membre (CHF)</span>
                <input className="input-field mt-1" type="number" value={s.prix_non_membre || 0}
                  onChange={maj(i, "prix_non_membre")} data-testid={`stage-prix-non-membre-${i}`} />
              </label>
              <div className="md:col-span-6 flex flex-wrap items-center gap-3">
                {termine(s)
                  ? <Badge ton="bleu">Terminé — archivé automatiquement</Badge>
                  : <Badge ton="vert">En cours ou à venir</Badge>}
                {!s.fin && (
                  <span className="text-xs" style={{ color: "#B45309" }}>
                    Indique le dernier jour pour que l'archivage se fasse tout seul
                  </span>
                )}
                <span className="text-xs text-[var(--muted)]">Identifiant : {s.id || "à créer"}</span>
                <button className="btn-ghost ml-auto" onClick={() => supprimer(i)}
                  data-testid={`stage-supprimer-${i}`}>
                  <Trash2 size={13} /> Supprimer la ligne
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <button className="btn-ghost" data-testid="stage-ajouter"
        onClick={() => setListe([...liste, { id: `stage_${Date.now()}`, nom: "", dates: "", fin: "", prix_membre: 0, prix_non_membre: 0 }])}>
        <Plus size={13} /> Ajouter un stage
      </button>

      <div className="rounded-2xl px-4 py-3 text-xs mt-3" data-testid="stages-mode-emploi"
        style={{ background: "var(--primary-tint)", color: "var(--text)" }}>
        <strong>Tu n'as rien à faire quand un stage se termine.</strong>
        <ul className="mt-2 space-y-1">
          <li>· Passé le <strong>dernier jour</strong>, la console archive le stage toute seule : il sort
            de la liste de travail. Ses prix, ses factures et ses inscriptions restent intacts.</li>
          <li>· Nouveau stage = <strong>nouvelle ligne</strong> (Pâques 2027, automne 2027…). On ne
            réécrit jamais une ancienne ligne : chaque facture garde le nom et le prix de son année.</li>
          <li>· « Supprimer la ligne » ne sert qu'à une erreur de saisie ou à un stage annulé avant toute
            inscription. Ne supprime jamais un stage qui a des factures : c'est cette ligne qui porte
            le prix.</li>
          <li>· Ce catalogue sert la <strong>console</strong>. Le site public affiche encore ses stages
            depuis son propre catalogue : les deux seront réunis à la bascule.</li>
        </ul>
      </div>
    </Section>
  );
}
